// Queue Record Enhancement Utilities
// This file provides a centralized place to add custom business logic to queue records

import { enhanceQueueRecordInput as baseEnhance } from "./cannedSearchCriteria";
import {
  validateGraphQLContract,
  logContractValidation,
  alertContractBreak,
} from "./contractValidator";

/**
 * Custom enhancement function for lead search queue records
 * This is the main place to add tenant-specific logic, business rules, etc.
 */
export const enhanceLeadSearchQueueRecord = (
  baseInput,
  startupName,
  userMode,
  additionalContext = {}
) => {
  // Start with the base enhancement
  let enhancedInput = baseEnhance(baseInput, startupName, userMode);

  // CUSTOM ENHANCEMENT POINT: Add your business logic here
  // This is where you can easily add tenant ID, entity type, and other metadata

  // Example: Add tenant-specific configuration
  enhancedInput = addTenantConfiguration(enhancedInput, startupName);

  // Example: Add business rules based on user mode
  enhancedInput = addBusinessRules(enhancedInput, userMode);

  // Example: Add custom metadata
  enhancedInput = addCustomMetadata(enhancedInput, additionalContext);

  // Example: Add processing instructions
  enhancedInput = addProcessingInstructions(enhancedInput);

  return enhancedInput;
};

/**
 * Filter the enhanced queue record to only include fields accepted by the GraphQL API
 * The API expects a simple structure, so we need to move rich metadata into the data field
 */
export const filterForGraphQLApi = (enhancedInput) => {
  // Extract the original search criteria from the data field
  const originalData = JSON.parse(enhancedInput.data);

  // Create a comprehensive metadata object that includes all the rich information
  const richMetadata = {
    // Original search criteria
    searchCriteria: originalData,

    // Tenant and entity information
    tenantId: enhancedInput.tenantId,
    entityType: "LGAPOLLOMGR", // Updated entity type
    userMode: enhancedInput.userMode,

    // Timestamps
    createdAt: enhancedInput.createdAt,
    updatedAt: enhancedInput.updatedAt,

    // Processing metadata
    priority: enhancedInput.priority,
    expectedProcessingTime: enhancedInput.expectedProcessingTime,

    // Source tracking
    source: enhancedInput.source,
    sourceScreen: enhancedInput.sourceScreen,

    // Business context
    businessContext: enhancedInput.businessContext,

    // Tenant configuration
    tenantConfiguration: enhancedInput.tenantConfiguration,

    // Business rules
    businessRules: enhancedInput.businessRules,

    // Custom metadata
    customMetadata: enhancedInput.customMetadata,

    // Processing instructions
    processingInstructions: enhancedInput.processingInstructions,
  };

  // Return only the fields that the GraphQL API accepts
  // Based on the error message, the API likely only accepts: name, status, data
  return {
    name: enhancedInput.name,
    status: enhancedInput.status,
    data: JSON.stringify(richMetadata), // Store all rich metadata in the data field
  };
};

/**
 * Add tenant-specific configuration to the queue record
 */
const addTenantConfiguration = (input, startupName) => {
  const tenantConfigs = {
    default: {
      priority: "medium",
      maxProcessingTime: "10 minutes",
      retryAttempts: 3,
    },
    // Add your startup-specific configurations here
    startup1: {
      priority: "high",
      maxProcessingTime: "5 minutes",
      retryAttempts: 5,
    },
    // Add more tenant configs as needed
  };

  const config = tenantConfigs[startupName] || tenantConfigs["default"];

  return {
    ...input,
    tenantConfiguration: config,
  };
};

/**
 * Add business rules based on user mode
 */
const addBusinessRules = (input, userMode) => {
  const businessRules = {
    default: {
      requireApproval: false,
      autoProcess: true,
      notificationLevel: "standard",
    },
    admin: {
      requireApproval: false,
      autoProcess: true,
      notificationLevel: "high",
    },
    demo: {
      requireApproval: true,
      autoProcess: false,
      notificationLevel: "low",
    },
  };

  const rules = businessRules[userMode] || businessRules["default"];

  return {
    ...input,
    businessRules: rules,
  };
};

/**
 * Add custom metadata to the queue record
 */
const addCustomMetadata = (input, additionalContext) => {
  return {
    ...input,
    customMetadata: {
      // Add any custom fields you need
      searchSource: "sales-process-dashboard",
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      sessionId: sessionStorage.getItem("sessionId") || "unknown",
      ...additionalContext,
    },
  };
};

/**
 * Add processing instructions for the backend
 */
const addProcessingInstructions = (input) => {
  return {
    ...input,
    processingInstructions: {
      // Instructions for the backend processor
      searchEngine: "apollo.io",
      maxResults: 100,
      includeContactInfo: true,
      includeCompanyInfo: true,
      filterDuplicates: true,
      enrichWithLinkedIn: true,
      exportFormat: "csv",
      notificationEmail: true,
    },
  };
};

/**
 * Comprehensive validation for queue records
 * Validates both enhanced and filtered records
 */
export const validateQueueRecord = (record, type = "filtered") => {
  const errors = [];

  if (type === "enhanced") {
    // Validate enhanced record structure
    const requiredEnhancedFields = [
      "name",
      "status",
      "tenantId",
      "entityType",
      "userMode",
    ];
    const missingEnhancedFields = requiredEnhancedFields.filter(
      (field) => !record[field]
    );

    if (missingEnhancedFields.length > 0) {
      errors.push(
        `Enhanced record missing required fields: ${missingEnhancedFields.join(
          ", "
        )}`
      );
    }

    // Validate data field is valid JSON
    if (record.data) {
      try {
        JSON.parse(record.data);
      } catch (error) {
        errors.push("Enhanced record data field must be valid JSON");
      }
    }

    // Validate business context
    if (!record.businessContext?.startupName) {
      errors.push("Enhanced record must have businessContext.startupName");
    }
  } else if (type === "filtered") {
    // Validate filtered record matches GraphQL schema
    return validateGraphQLSchema(record);
  }

  if (errors.length > 0) {
    throw new Error(
      `Queue Record Validation Failed (${type}):\n${errors.join("\n")}`
    );
  }

  return true;
};

/**
 * Validate that the filtered queue record matches the GraphQL schema
 * This ensures we catch schema violations before sending to the API
 * Uses contract validation for comprehensive checking
 */
export const validateGraphQLSchema = (filteredInput) => {
  // Use contract validation for comprehensive checking
  const validationResult = validateGraphQLContract(
    filteredInput,
    "CreateQueueRecordInput"
  );

  // Log the validation result
  logContractValidation(validationResult, "(Filtered Input)");

  // Alert if there are contract breaks
  alertContractBreak(validationResult);

  // Additional validation for Lambda compatibility
  validateLambdaCompatibility(filteredInput);

  // Throw error if validation failed
  if (!validationResult.isValid) {
    const errorMessages = validationResult.errors
      .map((error) => error.message)
      .join("\n");
    throw new Error(`GraphQL Contract Validation Failed:\n${errorMessages}`);
  }

  return true;
};

/**
 * Create a minimal data format for Lambda compatibility
 * This creates the simplest possible data structure to avoid Lambda parsing issues
 */
export const createMinimalDataFormat = (enhancedInput) => {
  // Extract the original search criteria from the data field
  const originalData = JSON.parse(enhancedInput.data);

  // Create a minimal structure with only the most essential fields
  const minimalData = {
    // Core search criteria (flattened to avoid nested objects)
    companySize: originalData.companySize,
    industry: originalData.industry,
    jobTitle: originalData.jobTitle,
    location: originalData.location,
    technologies: originalData.technologies,

    // Essential metadata
    tenantId: enhancedInput.tenantId,
    entityType: "LGAPOLLOMGR",

    // Basic processing info
    priority: enhancedInput.priority,
    source: enhancedInput.source,

    // Timestamps
    createdAt: enhancedInput.createdAt,
    updatedAt: enhancedInput.updatedAt,
  };

  return {
    name: enhancedInput.name,
    status: enhancedInput.status,
    data: JSON.stringify(minimalData),
  };
};

/**
 * Create a simplified data format for Lambda compatibility
 * This reduces the complexity of the data structure to avoid Lambda parsing issues
 */
export const createSimplifiedDataFormat = (enhancedInput) => {
  // Extract the original search criteria from the data field
  const originalData = JSON.parse(enhancedInput.data);

  // Create a simplified structure with only essential fields
  const simplifiedData = {
    // Core search criteria
    searchCriteria: originalData,

    // Essential metadata
    tenantId: enhancedInput.tenantId,
    entityType: "LGAPOLLOMGR",
    userMode: enhancedInput.userMode,

    // Timestamps
    createdAt: enhancedInput.createdAt,
    updatedAt: enhancedInput.updatedAt,

    // Basic processing info
    priority: enhancedInput.priority,
    source: enhancedInput.source,

    // Simplified business context
    businessContext: {
      startupName: enhancedInput.businessContext?.startupName,
      searchType: "apollo-lead-search",
      entityType: "LGAPOLLOMGR",
    },
  };

  return {
    name: enhancedInput.name,
    status: enhancedInput.status,
    data: JSON.stringify(simplifiedData),
  };
};

/**
 * Additional validation for Lambda function compatibility
 * This helps ensure the data format is compatible with the backend Lambda
 */
export const validateLambdaCompatibility = (filteredInput) => {
  console.group("🔍 Lambda Compatibility Check");

  // Check if data field is properly formatted
  if (filteredInput.data) {
    try {
      const parsedData = JSON.parse(filteredInput.data);
      console.log("✅ Data field is valid JSON");
      console.log("📋 Data structure:", Object.keys(parsedData));

      // Check for required fields in the data
      const requiredDataFields = ["searchCriteria", "tenantId", "entityType"];
      const missingDataFields = requiredDataFields.filter(
        (field) => !parsedData[field]
      );

      if (missingDataFields.length > 0) {
        console.warn("⚠️ Missing fields in data:", missingDataFields);
      } else {
        console.log("✅ All required data fields present");
      }
    } catch (error) {
      console.error("❌ Data field is not valid JSON:", error.message);
    }
  }

  // Log the complete input for debugging
  console.log("📤 Complete input being sent to Lambda:");
  console.log(JSON.stringify(filteredInput, null, 2));

  console.groupEnd();
};

/**
 * Log the enhanced queue record for debugging
 */
export const logEnhancedQueueRecord = (
  enhancedInput,
  action = "queue-lead-search"
) => {
  console.group(`🚀 Enhanced Queue Record for ${action}`);
  console.log("📋 Full Record:", enhancedInput);
  console.log("🏢 Tenant ID:", enhancedInput.tenantId);
  console.log("🏷️ Entity Type:", enhancedInput.entityType);
  console.log("⚙️ Business Rules:", enhancedInput.businessRules);
  console.log(
    "📊 Processing Instructions:",
    enhancedInput.processingInstructions
  );
  console.groupEnd();
};

/**
 * Test function to demonstrate validation
 * This can be called to test the validation system
 */
export const testQueueRecordValidation = () => {
  console.group("🧪 Testing Queue Record Validation");

  // Test 1: Valid enhanced record
  const validEnhancedRecord = {
    name: "Test Lead Search",
    status: "pending",
    data: '{"companySize":"11-50"}',
    tenantId: "test-tenant",
    entityType: "lead-search",
    userMode: "default",
    businessContext: {
      startupName: "test-startup",
    },
  };

  try {
    validateQueueRecord(validEnhancedRecord, "enhanced");
    console.log("✅ Test 1: Valid enhanced record passed");
  } catch (error) {
    console.error("❌ Test 1: Valid enhanced record failed:", error.message);
  }

  // Test 2: Invalid enhanced record (missing required fields)
  const invalidEnhancedRecord = {
    name: "Test Lead Search",
    status: "pending",
    // Missing tenantId, entityType, userMode, businessContext
  };

  try {
    validateQueueRecord(invalidEnhancedRecord, "enhanced");
    console.log("❌ Test 2: Invalid enhanced record should have failed");
  } catch (error) {
    console.log(
      "✅ Test 2: Invalid enhanced record correctly caught:",
      error.message
    );
  }

  // Test 3: Valid filtered record
  const validFilteredRecord = {
    name: "Test Lead Search",
    status: "pending",
    data: '{"searchCriteria":{"companySize":"11-50"},"tenantId":"test-tenant"}',
  };

  try {
    validateQueueRecord(validFilteredRecord, "filtered");
    console.log("✅ Test 3: Valid filtered record passed");
  } catch (error) {
    console.error("❌ Test 3: Valid filtered record failed:", error.message);
  }

  // Test 4: Invalid filtered record (extra fields)
  const invalidFilteredRecord = {
    name: "Test Lead Search",
    status: "pending",
    data: '{"searchCriteria":{"companySize":"11-50"}}',
    tenantId: "test-tenant", // This field should not be in filtered record
  };

  try {
    validateQueueRecord(invalidFilteredRecord, "filtered");
    console.log("❌ Test 4: Invalid filtered record should have failed");
  } catch (error) {
    console.log(
      "✅ Test 4: Invalid filtered record correctly caught:",
      error.message
    );
  }

  // Test 5: Invalid filtered record (invalid JSON)
  const invalidJsonRecord = {
    name: "Test Lead Search",
    status: "pending",
    data: "invalid json string",
  };

  try {
    validateQueueRecord(invalidJsonRecord, "filtered");
    console.log("❌ Test 5: Invalid JSON should have failed");
  } catch (error) {
    console.log("✅ Test 5: Invalid JSON correctly caught:", error.message);
  }

  console.groupEnd();
};

/**
 * Log the filtered queue record for debugging
 */
export const logFilteredQueueRecord = (
  filteredInput,
  action = "queue-lead-search"
) => {
  console.group(
    `📤 Filtered Queue Record for ${action} (GraphQL API Compatible)`
  );
  console.log("📋 API Input:", filteredInput);
  console.log(
    "📦 Rich Metadata in Data Field:",
    JSON.parse(filteredInput.data)
  );
  console.groupEnd();
};
